import React, { useState, useRef, useEffect } from "react";
import { Checkbox, Form, Input, Select } from "antd";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import ReactQuill from "react-quill";
import { ReactQuillOptions } from "../../../utils/react-quill-options";
import { openNotification } from "../../../shared/notification";
import { generateSlug } from "../../../utils/generate-slug";

// Container style for the map
const containerStyle = {
  width: "100%",
  height: "500px",
};

const Map = ({
  selectedAdddress,
  setSelectedAdddress,
  propertyStatusMessage,
  setPropertyStatusMessage,
  slug,
  setSlug,
  timestamp,
}) => {
  const autocompleteRef = useRef(null);
  const [markerPosition, setMarkerPosition] = useState({});
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const geocoder = useRef(null);
  let defaultCenter = {
    lat: 25.276987, // Latitude of Dubai
    lng: 55.296249, // Longitude of Dubai
  };

  // Initialize Google Maps API after it loads
  const onLoadGoogleMaps = () => {
    if (window.google && window.google.maps) {
      geocoder.current = new window.google.maps.Geocoder(); // Initialize geocoder
      setGoogleMapsLoaded(true); // Set the Google Maps as loaded
    }
  };

  // handlePlaceChanged on Selected
  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();

    if (place.geometry) {
      // Get latitude and longitude from the place geometry
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMarkerPosition(place.geometry.location);
      setSelectedAdddress({
        lat: lat,
        long: lng,
        address: place?.name,
        // formatted_address: place?.formatted_address,
      });
    } else {
      openNotification("Error", "Place details not available");
    }
  };

  // onChangeAddress for map
  const onChangeAddress = (e) => {
    setSelectedAdddress((prev) => ({
      ...prev,
      address: e.target.value,
    }));
  };

  // useEffect
  useEffect(() => {
    if (selectedAdddress.lat && selectedAdddress.long) {
      setMarkerPosition({
        lat: selectedAdddress.lat,
        lng: selectedAdddress.long,
      });
    } else {
      setMarkerPosition(defaultCenter);
    }
    // eslint-disable-next-line
  }, [selectedAdddress]);

  // Handle map click to select marker
  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarkerPosition({ lat, lng });
    // Perform reverse geocoding to get the address from lat/lng
    if (googleMapsLoaded && geocoder.current) {
      geocoder.current.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const address = results[0].formatted_address;
            setSelectedAdddress({
              lat,
              long: lng,
              address,
            });
          } else {
            openNotification("Error", "Address not found");
            setSelectedAdddress({
              lat,
              long: lng,
              address: "Unknown location",
            });
          }
        }
      );
    }
  };
  return (
    <div>
      {/* Title */}
      <Form.Item
        name="title"
        label="Title"
        rules={[{ required: true, message: "This field is required" }]}
        onChange={(e) => {
          const result = generateSlug(e.target.value);
          setSlug(result);
        }}
      >
        <Input />
      </Form.Item>
      {/* Custiom Slug */}
      <div className="ant-form-item css-dev-only-do-not-override-1b0bdye">
        <div className="ant-row ant-form-item-row css-dev-only-do-not-override-1b0bdye">
          <div className="ant-col ant-form-item-label css-dev-only-do-not-override-1b0bdye">
            <label>
              Slug (Will be generated automatically based on the title)
            </label>
          </div>
          <div>
            <div>
              <input
                className="ant-custom-input ant-custom-input"
                type="text"
                value={
                  timestamp !== false && timestamp !== ""
                    ? `${slug}-${timestamp}`.replace(/-+/g, "-")
                    : slug
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (timestamp !== false && timestamp !== "") {
                    const slugWithoutTimestamp = newValue.replace(
                      `-${timestamp}`,
                      ""
                    );
                    setSlug(slugWithoutTimestamp);
                  } else {
                    setSlug(newValue);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* </Form.Item> */}
      <div className="row-wrap-container">
        <Form.Item
          name="referenceNumber"
          label="Reference Number"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* Sale or Rent Price */}
        <Form.Item
          name="saleOrRentprice"
          label="Sale or Rent Price"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* Sale or Rent Price */}
        <Form.Item
          name="pricePrefix"
          label="Price Prefix Text (Example: Starting Form)"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* Sale or Rent Price */}
        <Form.Item
          name="pricePostfix"
          label="Price Postfix Text (Example: Per Month)"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* type */}
        <Form.Item
          name="type"
          label="Type"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            options={[
              { value: "", label: "Property Type" },
              { value: "Apartment", label: "Apartment" },
              { value: "Duplexes", label: "Duplexes" },
              { value: "Mansion", label: "Mansion" },
              { value: "PentHouse", label: "PentHouse" },
              { value: "Studio", label: "Studio" },
              { value: "Townhouse", label: "Townhouse" },
              { value: "Villa", label: "Villa" },
              { value: "bulk-rent-unit", label: "Bulk Rent Unit" },
              { value: "bulk-sale-unit", label: "Bulk Sale Unit" },
              { value: "business-center", label: "Business Center" },
              { value: "co-working-space", label: "Co Working Space" },
              { value: "factory", label: "Factory" },
              { value: "farm", label: "Farm" },
              { value: "full-floor", label: "Full Floor" },
              { value: "half-floor", label: "Half Floor" },
              { value: "labor-camp", label: "Labor Camp" },
              { value: "land", label: "Land" },
              { value: "office-space", label: "Office Space" },
              { value: "retail", label: "Retail" },
              { value: "shop", label: "Shop" },
              { value: "show-room", label: "Show Room" },
              { value: "staff-accommodation", label: "Staff Accommodation" },
              { value: "warehouse", label: "Warehouse" },
              { value: "whole-building", label: "Whole Building" },
            ]}
            placeholder="Property Type"
          />
        </Form.Item>
        {/* status */}
        <Form.Item
          name="status"
          label="Project Status"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            onChange={(e) => {
              setPropertyStatusMessage(e);
            }}
            options={[
              {
                label: <span>Status</span>,
                title: "Status",
                options: [
                  { value: "OFF_PLAN", label: "Off Plan" },
                  { value: "RENT", label: "Rent" },
                ],
              },

              {
                label: <span>Sales</span>,
                title: "Sales",
                options: [
                  { value: "SALE_OFF_PLAN", label: "Off-Plan" },
                  { value: "SALE", label: "Ready" },
                ],
              },
            ]}
          />
        </Form.Item>
        {/* status */}
        <Form.Item
          name="publishing_status"
          label="Publishing Status"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            options={[
              { value: "PUBLISHED", label: "Published" },
              { value: "DRAFT", label: "Draft" },
              { value: "UNPUBLISHED", label: "Unpublished" },
            ]}
          />
        </Form.Item>
        {/* status */}
        <Form.Item
          name="rental_period"
          label="Amount Type"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            options={[
              { value: "", label: "Please select" },
              { value: "sale", label: "Sale" },
              { value: "yearly", label: "Yearly" },
              { value: "monthly", label: "Monthly" },
              { value: "weekly", label: "Weekly" },
              { value: "daily", label: "Daily" },
            ]}
          />
        </Form.Item>
        {/* bedrooms */}
        <Form.Item
          name="bedrooms"
          label="Bedrooms"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* bathrooms */}
        <Form.Item
          name="bathrooms"
          label="Bathrooms"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* garagesOrParkingSpaces */}
        <Form.Item
          name="garagesOrParkingSpaces"
          label="Garages or Parking Spaces"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* area */}
        <Form.Item
          name="area"
          label="Area"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* areaPostfix */}
        <Form.Item
          name="areaPostfix"
          label="Area Postfix"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* developer */}
        <Form.Item
          name="developer"
          label="Developer"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            options={[
              { value: "none", label: "None" },
              { value: "Mr. Eight", label: "Mr. Eight" },
              { value: "Object 1", label: "Object 1" },
              { value: "Emaar", label: "Emaar" },
              { value: "Danube", label: "Danube" },
              { value: "Nakheel", label: "Nakheel" },
              { value: "Azizi", label: "Azizi" },
              { value: "Sobha", label: "Sobha" },
              { value: "Samana", label: "Samana" },
              { value: "Omniyat", label: "Omniyat" },
              { value: "Meraas", label: "Meraas" },
              { value: "Majid al Futtaim", label: "Majid al Futtaim" },
              { value: "Ellington", label: "Ellington" },
              { value: "Damac", label: "Damac" },
              { value: "Binghatti", label: "Binghatti" },
              { value: "LMD", label: "LMD" },
              { value: "Imtiaz", label: "Imtiaz" },
              { value: "City View", label: "City View" },
              { value: "SOL", label: "SOL" },
              { value: "Sankari", label: "Sankari" },
              { value: "H & H", label: "H & H" },
              { value: "Select", label: "Select" },
              { value: "Nshama", label: "Nshama" },
              { value: "Signature", label: "Signature" },
              { value: "Wasl", label: "Wasl" },
              { value: "Valores", label: "Valores" },
              { value: "Vincitore Realty", label: "Vincitore Realty" },
              { value: "Laya Developers", label: "Laya Developers" },
              { value: "Al Fajer Properties", label: "Al Fajer Properties" },
              { value: "Acube Developers", label: "Acube Developers" },
              { value: "Akshara Global", label: "Akshara Global" },
              { value: "Al Barari", label: "Al Barari" },
              {
                value: "Al Fahim Property Development",
                label: "Al Fahim Property Development",
              },
              { value: "Al Faraa Properties", label: "Al Faraa Properties" },
              { value: "Al Habtoor", label: "Al Habtoor" },
              { value: "Al Hamra", label: "Al Hamra" },
              {
                value: "Al Massaleh Real Estate Group",
                label: "Al Massaleh Real Estate Group",
              },
              {
                value: "Al Mazaya Holding Company",
                label: "Al Mazaya Holding Company",
              },
              { value: "Al Wasl", label: "Al Wasl" },
              { value: "Aldar", label: "Aldar" },
              { value: "Alef Group", label: "Alef Group" },
              { value: "Aqua Properties", label: "Aqua Properties" },
              {
                value: "Arabian Gulf Properties",
                label: "Arabian Gulf Properties",
              },
              { value: "Arada Developers", label: "Arada Developers" },
              { value: "Arenco", label: "Arenco" },
              { value: "Bloom Holding", label: "Bloom Holding" },
              { value: "Credo Investments", label: "Credo Investments" },
              { value: "Danube Properties", label: "Danube Properties" },
              { value: "Dar Al- Arkan", label: "Dar Al- Arkan" },
              {
                value: "Deyaar Development PJSC",
                label: "Deyaar Development PJSC",
              },
              { value: "District One", label: "District One" },
              { value: "Dubai Holding", label: "Dubai Holding" },
              { value: "Dubai Properties", label: "Dubai Properties" },
              { value: "Dubai South", label: "Dubai South" },
              { value: "Eagle Hills", label: "Eagle Hills" },
              { value: "Ellington Properties", label: "Ellington Properties" },
              { value: "EMAAR", label: "EMAAR" },
              {
                value: "Fakhruddin Properties",
                label: "Fakhruddin Properties",
              },
              {
                value: "Falconcity of Wonders",
                label: "Falconcity of Wonders",
              },
              { value: "Five Holdings", label: "Five Holdings" },
              { value: "G & Co", label: "G & Co" },
              {
                value: "G&CO REAL ESTATE DEVELOPMENT FZ LLC",
                label: "G&CO REAL ESTATE DEVELOPMENT FZ LLC",
              },
              { value: "Gemini Properties", label: "Gemini Properties" },
              { value: "Green Group", label: "Green Group" },
              { value: "Green Yard", label: "Green Yard" },
              {
                value: "GROVY REAL ESTATE DEVELOPMENT L.L.C",
                label: "GROVY REAL ESTATE DEVELOPMENT L.L.C",
              },
              { value: "H and H Development", label: "H and H Development" },
              {
                value: "HMB Homes Real Estate Development",
                label: "HMB Homes Real Estate Development",
              },
              { value: "IGO", label: "IGO" },
              { value: "Iman Developers", label: "Iman Developers" },
              {
                value: "International Merchant House Real Estate Dev. LLC",
                label: "International Merchant House Real Estate Dev. LLC",
              },
              {
                value: "Invest Group Overseas (IGO)",
                label: "Invest Group Overseas (IGO)",
              },
              {
                value: "Investment Corporation of Dubai",
                label: "Investment Corporation of Dubai",
              },
              { value: "Ithra developers", label: "Ithra developers" },
              { value: "Kappa Acca", label: "Kappa Acca" },
              { value: "Kleindienst Group", label: "Kleindienst Group" },
              { value: "KM Properties", label: "KM Properties" },
              { value: "Liv Developer", label: "Liv Developer" },
              { value: "Lootah", label: "Lootah" },
              { value: "Lucky 1 Residence", label: "Lucky 1 Residence" },
              { value: "Madain Properties", label: "Madain Properties" },
              { value: "Mag Group", label: "Mag Group" },
              { value: "Meydan", label: "Meydan" },
              {
                value: "Myra Real Estate Developments",
                label: "Myra Real Estate Developments",
              },
              { value: "Nabni Developers", label: "Nabni Developers" },
              { value: "One Zabeel", label: "One Zabeel" },
              { value: "Orion Group", label: "Orion Group" },
              { value: "Oro24", label: "Oro24" },
              { value: "Palma Holding", label: "Palma Holding" },
              { value: "Pantheon", label: "Pantheon" },
              { value: "Pantheon Development", label: "Pantheon Development" },
              { value: "PDLM Residential LLC", label: "PDLM Residential LLC" },
              {
                value: "Peace Home Development",
                label: "Peace Home Development",
              },
              { value: "Presscott", label: "Presscott" },
              { value: "Pure Gold", label: "Pure Gold" },
              { value: "Reportage", label: "Reportage" },
              {
                value: "RSG Group of Companies",
                label: "RSG Group of Companies",
              },
              { value: "Samana Developers", label: "Samana Developers" },
              { value: "Segrex Development", label: "Segrex Development" },
              { value: "Select Group", label: "Select Group" },
              { value: "Tabeer", label: "Tabeer" },
              { value: "Tecom Group", label: "Tecom Group" },
              { value: "The Cayan Group", label: "The Cayan Group" },
              { value: "The first Group", label: "The first Group" },
              { value: "Tiger Group", label: "Tiger Group" },
              { value: "Time Properties", label: "Time Properties" },
              { value: "TownX", label: "TownX" },
              { value: "Union Properties", label: "Union Properties" },
              {
                value: "Wasl Asset Management Group",
                label: "Wasl Asset Management Group",
              },
              { value: "wasl one", label: "wasl one" },
              {
                value: "WESTAR PROPERTIES LIMITED",
                label: "WESTAR PROPERTIES LIMITED",
              },
              { value: "Zazen One Apartments", label: "Zazen One Apartments" },
            ]}
            placeholder=""
          />
        </Form.Item>
        {/* offering_type */}
        <Form.Item
          name="offering_type"
          label="Offering Type "
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            options={[
              { value: "none", label: "None" },
              { value: "commercial", label: "Commercial" },
              { value: "residential", label: "Residential" },
            ]}
            placeholder=""
          />
        </Form.Item>
        {/* finishing_type */}
        <Form.Item
          name="finishing_type"
          label="Finishing Type "
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            options={[
              { value: "", label: "Please select" },
              { value: "unfinished", label: "Unfinished" },
              { value: "semi-finished", label: "Semi-Finished" },
              { value: "fully-finished", label: "Fully-Finished" },
            ]}
            placeholder=""
          />
        </Form.Item>
        {/* uae_emirate */}
        <Form.Item
          name="uae_emirate"
          label="UAE Emirate "
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Select
            options={[
              { value: "", label: "Please select" },
              { value: "dubai", label: "Dubai" },
              { value: "abu_dhabi", label: "Abu Dhabi" },
              { value: "northern_emirates", label: "Northern Emirates" },
            ]}
            placeholder=""
          />
        </Form.Item>
        {/* handoverDate */}
        <Form.Item
          name="handoverDate"
          label="Handover Date"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
      </div>
      <div>
        {/* off plan */}
        {propertyStatusMessage === "OFF_PLAN" && (
          <div>
            <h4 style={{ margin: "10px 0px 20px 0px" }}>
              Check Property Availability
            </h4>
            <Form.Item
              name="soldOut"
              valuePropName="checked" // This binds the checkbox value to the form data
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Checkbox>Sold Out</Checkbox>
            </Form.Item>
            <Form.Item
              name="propertyStatusMessage"
              label="Message to show on frontend tag e.g Sold"
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Input placeholder="" />
            </Form.Item>
          </div>
        )}
        {/* RENT */}
        {propertyStatusMessage === "RENT" && (
          <div>
            <h4 style={{ margin: "10px 0px 20px 0px" }}>
              Check Property Availability
            </h4>
            <Form.Item
              name="soldOut"
              valuePropName="checked" // This binds the checkbox value to the form data
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Checkbox>Rented</Checkbox>
            </Form.Item>
            <Form.Item
              name="propertyStatusMessage"
              label="Message to show on frontend tag e.g Rented"
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Input placeholder="" />
            </Form.Item>
          </div>
        )}
        {/* SALE */}
        {(propertyStatusMessage === "SALE_OFF_PLAN" ||
          propertyStatusMessage === "SALE") && (
          <div>
            <h4 style={{ margin: "10px 0px 20px 0px" }}>
              Check Property Availability
            </h4>
            <Form.Item
              name="soldOut"
              valuePropName="checked" // This binds the checkbox value to the form data
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Checkbox>SALE</Checkbox>
            </Form.Item>
            <Form.Item
              name="propertyStatusMessage"
              label="Message to show on frontend tag e.g Sold"
              rules={[{ required: false, message: "This field is required" }]}
            >
              <Input placeholder="" />
            </Form.Item>
          </div>
        )}
      </div>
      {/* Meta Data Details*/}
      <h4 style={{ margin: "10px 0px 20px 0px" }}>Meta Details</h4>
      <div className="row-wrap-container">
        {/* Meta Title*/}
        <Form.Item
          name="meta_title"
          label="Meta Title"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
        {/* Sale or Rent Price */}
        {/* <Form.Item
          name="meta_description"
          label="Meta Description"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item> */}

        {/* Sale or Rent Price */}
        <Form.Item
          name="meta_keyword"
          label="Meta Keywords"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
      </div>
      <div>
        <Form.Item
          name="meta_description"
          label="Meta Description"
          rules={[{ required: false, message: "Description is required" }]}
        >
          <ReactQuill theme="snow" modules={ReactQuillOptions} />
        </Form.Item>
      </div>
      {/* <h4 style={{ margin: "10px 0px 20px 0px" }}>
        Mark this property as unpublished
      </h4> */}
      {/* <Form.Item
        name="isPropertyUnPublished"
        valuePropName="checked"
        rules={[{ required: false, message: "This field is required" }]}
      >
        <Checkbox>Remove property from public view</Checkbox>
      </Form.Item> */}
      <h4 style={{ margin: "10px 0px 20px 0px" }}>
        Mark This Property as Featured
      </h4>
      <Form.Item
        name="isFeatured"
        valuePropName="checked"
        rules={[{ required: false, message: "This field is required" }]}
      >
        <Checkbox>Mark this property as featured property</Checkbox>
      </Form.Item>

      {/* description */}
      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: false, message: "Description is required" }]}
      >
        <ReactQuill theme="snow" modules={ReactQuillOptions} />
      </Form.Item>

      {/* Map */}
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_FRONTED_MAP_KEY}
        libraries={["places"]}
        onLoad={onLoadGoogleMaps}
      >
        {/* Search input */}
        <Form.Item
          name="address"
          label="Address"
          rules={[{ required: false, message: "This field is required" }]}
        >
          <Autocomplete
            onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
            onPlaceChanged={handlePlaceChanged}
          >
            <Input
              value={selectedAdddress?.address}
              type="text"
              placeholder="Search for a location"
              onChange={onChangeAddress}
            />
          </Autocomplete>
        </Form.Item>

        {/* Google Map */}
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={markerPosition}
          zoom={12}
          onClick={handleMapClick} // Handle the map click event
        >
          <Marker position={markerPosition} />
        </GoogleMap>
      </LoadScript>
    </div>
  );
};

export default Map;
